import React, { Component } from 'react';
import { Box, Paper, Stack, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import { VictoryChart, VictoryLine, VictoryAxis, VictoryScatter } from 'victory';
import { getFunnelTimeseries, SESSION_EXPIRED } from '../services/funnelService';

// Séries quotidiennes du funnel (B8_02, contrat C7 — B6_04). Quatre séries qui DÉCOMPOSENT les
// barres du tunnel (D27) : même cohorte, même fenêtre, comptées en SITES. Patron de rendu :
// `BlocsAffiches.js` (Victory déjà payé, ~74 kB gzip — M10).
//
// Composant AUTONOME : il fait son propre appel après montage (ni React.lazy ni import()
// dynamique) et vit HORS du gate `{!isLoading && !error && data}` de `Funnel.js` — une erreur de
// l'agrégat ne le démonte pas, un « Rafraîchir » non plus (exigence du ticket : jamais
// détruit-remonté ; le remount se prouve par `data-mount-count`, compteur de module monotone).
// Les props `from`/`to`/`segment` sont la fenêtre SERVIE par l'agrégat (`servedLabels`), jamais
// la saisie en cours — c'est ce qui tient l'objectif « une seule sélection pilote l'agrégat et
// la courbe ». `reloadKey` change à chaque fetch du parent : un « Rafraîchir » à fenêtre
// identique recharge quand même la courbe.

// Assignation FIXE série → couleur/forme, jamais recalculée sur le nombre de séries visibles.
// Palette validée par le script de la skill `dataviz` (6 checks PASS, pire paire adjacente
// ΔE 11,5 protan / 28,6 vision normale, contraste ≥ 3:1 sur fond clair). Les FORMES de points
// sont l'encodage secondaire : l'écran est consulté pour des points budget et doit rester
// lisible imprimé en noir et blanc — la couleur seule ne porte jamais l'identité.
// ⚠ « sur la période » (L17b) : la série `deactivations` est bornée à la fenêtre, alors que la
// carte « Désinstallations » de l'agrégat compte depuis l'ouverture du suivi — les deux chiffres
// coexistent, chacun avec son libellé. `sum(series.deactivations) ≤ carte` est ATTENDU.
// `offsetH` = décalage intra-journée en HEURES (le point reste dans SA journée : ±8,4 h =
// ±0,35 jour). Dimensionné sur la géométrie réelle (revue 🟡-4) : 826 px de tracé / 91 jours
// ≈ 9,2 px/jour — un décalage plus petit laisse les 4 points empilés, et `inscriptions` /
// `activations` tombent le même jour 168 fois sur 174 (D29). L'arbitrage final est visuel
// (recette Mathias) — ne pas resserrer sans capture à l'appui.
export const SERIES = [
  { key: 'inscriptions', label: 'Inscriptions', color: '#1976d2', symbol: 'circle', offsetH: -8.4 },
  { key: 'plugin_connect', label: 'Connexions plugin', color: '#ed6c02', symbol: 'square', offsetH: -2.8 },
  { key: 'activations', label: 'Activations', color: '#00897b', symbol: 'triangleUp', offsetH: 2.8 },
  { key: 'deactivations', label: 'Désinstallations — sur la période', color: '#d32f2f', symbol: 'diamond', offsetH: 8.4 }
];

const DAY_LABEL = { fontSize: 11 };
const HOUR_MS = 60 * 60 * 1000;

// Slug d'ancre : on PRÉSERVE `.`, `_` et `-` (les versions semver restent lisibles dans les
// assertions de recette) ; tout le reste devient `-`. `plugin_version` est une chaîne remontée
// par le plugin, pas une valeur contrôlée — sans normalisation, une version exotique casserait
// le sélecteur `[data-testid="funnel-timeseries-mark-<version>"]`.
export const slugVersion = (v) => String(v).replace(/[^a-zA-Z0-9._-]+/g, '-');

// `yyyy-MM-dd` → `dd/MM/yyyy` (mentions en marge, D28). Comparaison et découpe en CHAÎNES :
// les jours servis par C7 sont déjà des clés UTC, les passer par `new Date` réintroduirait le
// fuseau de la machine.
export function formatJourFr(jour) {
  const parts = String(jour || '').split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(jour || '');
}

// Les TROIS cas de position d'un repère de version par rapport à l'axe (ticket §Repères) :
//   A (surAxe)  — days[0] ≤ first_seen ≤ days[n-1] → repère vertical sur l'axe ;
//   B (enMarge) — first_seen < days[0]             → mention « en service depuis » (D28) ;
//   C (ignorees)— first_seen > days[n-1]           → rien (une version née APRÈS la fenêtre ne
//                 porte aucune information pertinente pour elle — règle déduite de D28).
// `first_seen` est un JOUR `yyyy-MM-dd` (servi ainsi par le contrôleur, exprès) : la comparaison
// lexicographique des chaînes ISO suffit, aucun objet Date en jeu.
export function splitVersionMarks(marks, days) {
  const list = Array.isArray(marks) ? marks : [];
  const axis = Array.isArray(days) ? days : [];
  if (!axis.length) return { surAxe: [], enMarge: [], ignorees: list.slice() };
  const premier = axis[0];
  const dernier = axis[axis.length - 1];
  const surAxe = [];
  const enMarge = [];
  const ignorees = [];
  list.forEach((m) => {
    const jour = m && m.first_seen;
    if (typeof jour !== 'string' || !jour) {
      // Le contrat C7 garantit l'absence de version vide ; un document malformé est ignoré
      // plutôt que de fabriquer un repère sans date.
      ignorees.push(m);
    } else if (jour < premier) {
      enMarge.push(m);
    } else if (jour > dernier) {
      ignorees.push(m);
    } else {
      surAxe.push(m);
    }
  });
  return { surAxe, enMarge, ignorees };
}

// `days[] + series[clé][]` → points Victory. Les JOURS À ZÉRO SONT CONSERVÉS (l'axe continu
// vient du serveur, le filtrer fabriquerait de fausses tendances) ; une valeur absente ou
// illisible vaut 0 — jamais un trou.
export function toPoints(days, serie) {
  const axis = Array.isArray(days) ? days : [];
  const values = Array.isArray(serie) ? serie : [];
  return axis.map((jour, i) => {
    const n = Number(values[i]);
    return { x: new Date(`${jour}T00:00:00Z`), y: Number.isFinite(n) ? n : 0 };
  });
}

// Traduction des erreurs de `/funnel/timeseries` (L9). Deux FORMES d'erreur, pas une :
//   • `rejectGcCode` lève une erreur TYPÉE (`code`) — à tester AVANT de parser le message ;
//   • `http.js` lève `HTTP <status>: <corps brut>` — le corps utile est `{"msg": …}`.
// `message: null` = ne RIEN afficher dans la zone du graphe : sur une session expirée le parent
// affiche déjà son alerte dédiée (`funnel-session-expired`), deux messages se contrediraient.
// Parsing minimal LOCAL, patron `parseApiError` (`moderationDashboardService.js:66`) — pas
// d'import : ce serait une dépendance composant-funnel → service-modération injustifiée, et le
// `msg` du contrôleur funnel n'est de toute façon pas dans les champs que `parseApiError` lit.
export function describeTimeseriesError(err) {
  const GENERIQUE = 'Impossible de charger les séries quotidiennes.';
  if (!err) return { message: GENERIQUE };
  if (err.code === SESSION_EXPIRED) return { message: null };
  const match = /^HTTP (\d{3}):\s*([\s\S]*)$/.exec(String(err.message || ''));
  if (match) {
    let msg = null;
    try {
      const body = JSON.parse(match[2]);
      msg = body && body.msg;
    } catch (e) {
      msg = null;
    }
    if (msg === 'period too large') {
      return { message: 'Période trop longue pour le graphe quotidien — 400 jours maximum.' };
    }
    if (msg === 'wrong dates') {
      return { message: 'Dates inversées : la date de début est postérieure à la date de fin.' };
    }
  }
  // Tout le reste (dont `code: 'unexpected_response'`) : repli générique, sans inventer de cause.
  return { message: GENERIQUE };
}

// Compteur de MODULE, monotone, incrémenté à chaque montage : l'oracle du non-démontage de la
// recette (`data-mount-count` inchangé après « Rafraîchir » = le composant a survécu ; sa seule
// présence ne prouverait rien, un remount le rend aussi).
let mountCount = 0;

// Glyphe de légende : la FORME du point, pas seulement sa couleur (lisibilité N&B).
function renderGlyph(symbol, color) {
  const props = { fill: color };
  return (
    <svg width="12" height="12" aria-hidden="true">
      {symbol === 'circle' && <circle cx="6" cy="6" r="4.5" {...props} />}
      {symbol === 'square' && <rect x="1.5" y="1.5" width="9" height="9" {...props} />}
      {symbol === 'triangleUp' && <polygon points="6,1 11,10.5 1,10.5" {...props} />}
      {symbol === 'diamond' && <polygon points="6,0.5 11.5,6 6,11.5 0.5,6" {...props} />}
    </svg>
  );
}

class FunnelTimeseries extends Component {
  constructor(props) {
    super(props);
    this.state = { data: null, isLoading: false, error: null };
    this._req = 0;
    // Incrément au CONSTRUCTEUR et non dans componentDidMount : le premier render doit déjà
    // porter la bonne valeur (sinon `data-mount-count` affiche « 0 » jusqu'au setState suivant).
    // Sûr ici : `src/index.js` est sans StrictMode ni mode concurrent — une instance construite
    // est une instance montée.
    mountCount += 1;
    this._mountCount = mountCount;
    this.fetchSeries = this.fetchSeries.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchSeries();
  }

  componentDidUpdate(prevProps) {
    const { from, to, segment, reloadKey } = this.props;
    if (
      prevProps.from !== from ||
      prevProps.to !== to ||
      prevProps.segment !== segment ||
      prevProps.reloadKey !== reloadKey
    ) {
      this.fetchSeries();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchSeries() {
    const { from, to, segment } = this.props;
    // Garde anti-course : seule la DERNIÈRE requête émise a le droit d'écrire le state — une
    // réponse lente arrivée après un changement de fenêtre serait attribuée à la mauvaise
    // période (même classe de bug que la garde `website_id` de BlocsAffiches).
    const req = ++this._req;
    this.setState({ isLoading: true, error: null });
    getFunnelTimeseries(from, to, segment)
      .then((result) => {
        if (!this._isMounted || req !== this._req) return;
        this.setState({ data: result, isLoading: false });
      })
      .catch((err) => {
        if (!this._isMounted || req !== this._req) return;
        this.setState({ error: err, isLoading: false, data: null });
      });
  }

  renderChart(days, series, surAxe, maxY) {
    const nf = new Intl.NumberFormat('fr-FR');
    const tickFormat = (t) => {
      const d = t instanceof Date ? t : new Date(t);
      return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    };
    // Fenêtre d'un seul jour (Du = Au) : un domaine [t, t] est dégénéré — on l'élargit d'une
    // demi-journée de chaque côté pour que le point et son éventuel repère restent visibles.
    const x0 = new Date(`${days[0]}T00:00:00Z`).getTime();
    const x1 = new Date(`${days[days.length - 1]}T00:00:00Z`).getTime();
    const pad = x0 === x1 ? 12 * HOUR_MS : 0;
    const xDomain = [new Date(x0 - pad), new Date(x1 + pad)];
    // Comptes de sites : jamais de tick décimal. Sur un petit max, Victory graduerait à 0,5 —
    // un tickFormat qui rend '' y laisserait une graduation nue : on impose les entiers.
    const yTicks = maxY <= 6 ? Array.from({ length: maxY + 1 }, (_, i) => i) : undefined;
    // Un seul calcul de points par série, réutilisé par la ligne ET les marqueurs.
    const pointsByKey = SERIES.reduce((acc, s) => {
      acc[s.key] = toPoints(days, series[s.key]);
      return acc;
    }, {});

    return (
      <VictoryChart
        width={900}
        height={300}
        scale={{ x: 'time' }}
        domain={{ x: xDomain, y: [0, maxY] }}
        padding={{ top: 16, bottom: 40, left: 50, right: 24 }}
      >
        <VictoryAxis tickFormat={tickFormat} fixLabelOverlap style={{ tickLabels: DAY_LABEL }} />
        <VictoryAxis
          dependentAxis
          tickValues={yTicks}
          tickFormat={(t) => (Number.isInteger(t) ? nf.format(t) : '')}
          style={{ tickLabels: DAY_LABEL }}
        />
        {/* Repères de version (cas A) : trait vertical neutre — le détail (version + date) est
            porté par les puces sous le graphe, qui sont aussi les ancres de recette. */}
        {surAxe.map((m) => (
          <VictoryLine
            key={`mark-${m.version}`}
            data={[
              { x: new Date(`${m.first_seen}T00:00:00Z`), y: 0 },
              { x: new Date(`${m.first_seen}T00:00:00Z`), y: maxY }
            ]}
            style={{ data: { stroke: '#616161', strokeWidth: 1, strokeDasharray: '4,4' } }}
          />
        ))}
        {/* Une ligne fine par série (zéros compris — l'axe reste honnête) + les POINTS des jours
            non nuls, en formes distinctes et légèrement décalés dans la journée : sans le
            décalage, `activations` masque `inscriptions` (168 des 174 activations tombent le
            jour même de l'inscription, D29 — c'est l'écart qui porte l'information). Ni lissage
            ni interpolation : ligne linéaire sur données complètes. */}
        {SERIES.map((s) => (
          <VictoryLine
            key={`line-${s.key}`}
            data={pointsByKey[s.key]}
            style={{ data: { stroke: s.color, strokeWidth: 1, strokeOpacity: 0.35 } }}
          />
        ))}
        {SERIES.map((s) => (
          <VictoryScatter
            key={`dots-${s.key}`}
            data={pointsByKey[s.key]
              .filter((p) => p.y > 0)
              .map((p) => ({ x: new Date(p.x.getTime() + s.offsetH * HOUR_MS), y: p.y }))}
            size={3}
            symbol={s.symbol}
            style={{ data: { fill: s.color } }}
          />
        ))}
      </VictoryChart>
    );
  }

  render() {
    // `testid` en PROP (défaut : seul appelant actuel) — piège maison : un testid figé dans un
    // composant réutilisé cesse d'être un oracle dès le second appelant.
    const { testid = 'funnel-timeseries' } = this.props;
    const { data, isLoading, error } = this.state;

    const days = data && Array.isArray(data.days) ? data.days : [];
    const series = (data && data.series) || {};
    const { surAxe, enMarge } = splitVersionMarks(data && data.version_marks, days);
    const values = SERIES.reduce((acc, s) => acc.concat(Array.isArray(series[s.key]) ? series[s.key] : []), []);
    const maxY = Math.max(1, ...values.map((v) => Number(v) || 0));
    // `days` ABSENT compte comme vide : une réponse tronquée (objet sans `days` — `rejectGcCode`
    // laisse passer tout objet sans gcCode) ne doit jamais atteindre `renderChart`, où un axe
    // vide fabriquerait un Invalid Date dans le domaine et emporterait l'écran entier (🟡-3).
    const vide = days.length === 0 || values.every((v) => !Number(v));
    const erreur = error ? describeTimeseriesError(error) : null;
    // État MACHINE du conteneur (🟡-2) : sans lui, les compteurs à « 0 » du premier rendu
    // (pendant le chargement) sont indiscernables de « chargé, zéro repère » — la recette
    // pourrait passer au faux vert. Les assertions s'ancrent sur `data-state`.
    const etat = error ? 'error' : !data ? 'loading' : vide ? 'empty' : 'ready';

    return (
      <Paper
        variant="outlined"
        sx={{ p: 2 }}
        data-testid={testid}
        // Les 4 attributs d'état sont rendus dans TOUS les états (nominal, vide, erreur) et en
        // chiffres BRUTS (jamais toLocaleString : U+202F) — ce sont les oracles de la recette,
        // à ne lire QUE quand `data-state` a quitté `loading`.
        data-state={etat}
        data-days={String(days.length)}
        data-marks-axis={String(surAxe.length)}
        data-marks-margin={String(enMarge.length)}
        data-mount-count={String(this._mountCount || 0)}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1">Séries quotidiennes</Typography>
          {data && (
            <Typography component="span" variant="caption" color="text.secondary">
              ({data.from} → {data.to})
            </Typography>
          )}
        </Stack>
        {/* Honnêteté de périmètre (ticket §Repères) : courbes = cohorte de la période, comptées
            en sites (D27) ; repères de version = PARC ENTIER (L16) — un repère peut tomber un
            jour où les quatre courbes sont à zéro. Fuseau UTC (invariant B6_04). */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Courbes : communautés de la cohorte de la période, comptées en sites · Repères de
          version : parc entier, toutes cohortes confondues · dates en UTC
        </Typography>

        {isLoading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && erreur && erreur.message && (
          <Alert severity="error" data-testid={`${testid}-error`}>
            {erreur.message}
          </Alert>
        )}

        {!error && data && (
          <>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              {SERIES.map((s) => (
                <Stack
                  key={s.key}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  data-testid={`${testid}-serie-${s.key}`}
                >
                  {renderGlyph(s.symbol, s.color)}
                  <Typography variant="caption">{s.label}</Typography>
                </Stack>
              ))}
            </Stack>

            {vide ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
                data-testid={`${testid}-empty`}
              >
                Aucun événement sur la période.
              </Typography>
            ) : (
              this.renderChart(days, series, surAxe, maxY)
            )}

            {(surAxe.length > 0 || enMarge.length > 0) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {surAxe.map((m) => (
                  <Chip
                    key={`mark-${m.version}`}
                    size="small"
                    variant="outlined"
                    data-testid={`${testid}-mark-${slugVersion(m.version)}`}
                    label={`v${m.version} — sortie le ${formatJourFr(m.first_seen)}`}
                  />
                ))}
                {/* D28 — une version sortie AVANT la période reste dite, en marge : la
                    fonctionnalité ne disparaît pas au raccourci « 30 j ». */}
                {enMarge.map((m) => (
                  <Chip
                    key={`margin-${m.version}`}
                    size="small"
                    variant="outlined"
                    color="default"
                    data-testid={`${testid}-margin-${slugVersion(m.version)}`}
                    label={`v${m.version} en service depuis le ${formatJourFr(m.first_seen)}`}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Paper>
    );
  }
}

export default FunnelTimeseries;
